<template>
	<div class="timecode">
		<div class="timecode__header">
			<h2 class="timecode__header-title">{{ $t('timecodes') }}</h2>
		</div>
		<div class="timecode__content">
			<ol class="timecode__list">
				<li v-for="chapter in chaptersData" class="timecode__item">
					<a class="timecode__link" @click="rewindTo(chapter)">
						<span class="timecode__descr" v-html="chapter.name"></span>
						<span class="timecode__time">{{chapter.time_human}}</span>
					</a>
				</li>
			</ol>
		</div>
	</div>
</template>

<script>
import "videojs-markers";
import "videojs-markers/dist/videojs.markers.css";

export default {
	name: "AudioTimeCode",

	props: {
		chapters: Array,
	},

	data() {
		return {
			chaptersData: this.chapters,
			player: null,
			progressHolderEl: null,
			segmentTooltipEl: null,
			segmentHighlightEl: null,
			onProgressMouseMove: null,
			onProgressMouseLeave: null,
		};
	},

	mounted() {
		this.player = window.audioPlayer;

		if (this.player && this.player.markers && this.chaptersData && this.chaptersData.length > 0) {
			try {
				if (this.player.markers.reset) {
					this.player.markers.reset([]);
				}

				this.player.markers({
					markers: this.chaptersData.map(chapter => ({
						time: chapter.time_in_sec,
						text: chapter.name
					})),
					markerStyle: {
						width: "2px",
						backgroundColor: "#d2cfcf",
						borderRadius: "1px",
					},
					markerTip: {
						display: false,
						text: marker => marker.text,
					},
				});

				this.setupSegmentTooltip();
			} catch (error) {
				console.warn("Failed to add markers to audio player:", error);
			}
		}
	},

	beforeDestroy() {
		this.destroySegmentTooltip();
	},

	methods: {
		setupSegmentTooltip() {
			if (!this.player || !this.player.controlBar || !this.player.controlBar.progressControl) return;

			this.progressHolderEl = this.player.controlBar.progressControl.el().querySelector(".vjs-progress-holder");
			if (!this.progressHolderEl) return;
			this.progressHolderEl.style.overflow = "visible";

			this.segmentTooltipEl = document.createElement("div");
			this.segmentTooltipEl.className = "vjs-segment-tooltip";
			const segmentTooltipTextEl = document.createElement("span");
			segmentTooltipTextEl.className = "vjs-segment-tooltip__text";
			this.segmentTooltipEl.appendChild(segmentTooltipTextEl);
			this.progressHolderEl.appendChild(this.segmentTooltipEl);

			this.segmentHighlightEl = document.createElement("div");
			this.segmentHighlightEl.className = "vjs-segment-highlight";
			this.progressHolderEl.appendChild(this.segmentHighlightEl);

			this.onProgressMouseMove = (event) => {
				this.showSegmentTooltip(event);
			};
			this.onProgressMouseLeave = () => {
				this.hideSegmentTooltip();
			};

			this.progressHolderEl.addEventListener("mousemove", this.onProgressMouseMove);
			this.progressHolderEl.addEventListener("mouseleave", this.onProgressMouseLeave);
		},

		destroySegmentTooltip() {
			if (this.progressHolderEl && this.onProgressMouseMove) {
				this.progressHolderEl.removeEventListener("mousemove", this.onProgressMouseMove);
			}

			if (this.progressHolderEl && this.onProgressMouseLeave) {
				this.progressHolderEl.removeEventListener("mouseleave", this.onProgressMouseLeave);
			}

			if (this.segmentTooltipEl && this.segmentTooltipEl.parentNode) {
				this.segmentTooltipEl.parentNode.removeChild(this.segmentTooltipEl);
			}

			if (this.segmentHighlightEl && this.segmentHighlightEl.parentNode) {
				this.segmentHighlightEl.parentNode.removeChild(this.segmentHighlightEl);
			}

			this.progressHolderEl = null;
			this.segmentTooltipEl = null;
			this.segmentHighlightEl = null;
			this.onProgressMouseMove = null;
			this.onProgressMouseLeave = null;
		},

		showSegmentTooltip(event) {
			if (!this.player || !this.segmentTooltipEl || !this.progressHolderEl || !this.chaptersData || this.chaptersData.length === 0) return;

			const duration = this.player.duration();
			if (!duration || Number.isNaN(duration)) {
				this.hideSegmentTooltip();
				return;
			}

			const holderRect = this.progressHolderEl.getBoundingClientRect();
			if (!holderRect.width) {
				this.hideSegmentTooltip();
				return;
			}

			const pointerX = Math.min(Math.max(event.clientX - holderRect.left, 0), holderRect.width);
			const hoveredTime = (pointerX / holderRect.width) * duration;
			const hoveredChapter = this.findHoveredChapter(hoveredTime);

			if (!hoveredChapter) {
				this.hideSegmentTooltip();
				return;
			}

			const tooltipTextEl = this.segmentTooltipEl.querySelector(".vjs-segment-tooltip__text");
			if (!tooltipTextEl) return;

			tooltipTextEl.textContent = hoveredChapter.name;
			this.segmentTooltipEl.classList.add("is-visible");

			this.segmentTooltipEl.style.left = `${pointerX}px`;

			this.showSegmentHighlight(hoveredChapter, duration);
		},

		hideSegmentTooltip() {
			if (!this.segmentTooltipEl) return;

			this.segmentTooltipEl.classList.remove("is-visible");
			if (this.segmentHighlightEl) {
				this.segmentHighlightEl.classList.remove("is-visible");
			}
		},

		showSegmentHighlight(hoveredChapter, duration) {
			if (!this.segmentHighlightEl || !hoveredChapter || !duration) return;

			const sortedChapters = [...this.chaptersData]
				.filter(chapter => chapter && typeof chapter.time_in_sec === "number")
				.sort((a, b) => a.time_in_sec - b.time_in_sec);

			const currentIndex = sortedChapters.findIndex(chapter => chapter.time_in_sec === hoveredChapter.time_in_sec && chapter.name === hoveredChapter.name);
			if (currentIndex < 0) return;

			const nextChapter = sortedChapters[currentIndex + 1];
			const startTime = hoveredChapter.time_in_sec;
			const endTime = nextChapter ? nextChapter.time_in_sec : duration;
			const leftPercent = Math.max(0, Math.min(100, (startTime / duration) * 100));
			const widthPercent = Math.max(0, Math.min(100 - leftPercent, ((endTime - startTime) / duration) * 100));

			this.segmentHighlightEl.style.left = `${leftPercent}%`;
			this.segmentHighlightEl.style.width = `${widthPercent}%`;
			this.segmentHighlightEl.classList.add("is-visible");
		},

		findHoveredChapter(hoveredTime) {
			const sortedChapters = [...this.chaptersData]
				.filter(chapter => chapter && typeof chapter.time_in_sec === "number")
				.sort((a, b) => a.time_in_sec - b.time_in_sec);

			if (sortedChapters.length === 0) return null;

			for (let index = 0; index < sortedChapters.length; index += 1) {
				const chapter = sortedChapters[index];
				const nextChapter = sortedChapters[index + 1];
				const start = chapter.time_in_sec;
				const end = nextChapter ? nextChapter.time_in_sec : Number.POSITIVE_INFINITY;

				if (hoveredTime >= start && hoveredTime < end) {
					return chapter;
				}
			}

			return null;
		},

		rewindTo(chapter) {
			const player = window.audioPlayer;
			if (!player) {
				console.error("Audio player not found");
				return;
			}

			const stub = document.querySelector(".j-hide-audio-stub");
			if (stub && !stub.classList.contains("not-showing")) {
				stub.classList.add("not-showing");
				setTimeout(() => {
					stub.classList.add("hide");
				}, 600);
			}

			if (player.currentTime) {
				player.currentTime(chapter.time_in_sec);
			}

			if (player.play) {
				player.play();
			}
		},
	},
};
</script>

<style>
.lecture-audio .vjs-segment-tooltip {
	position: absolute;
	left: 0;
	bottom: 16px;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	width: auto;
	padding: 4px 8px;
	border-radius: 6px;
	font-size: 12px;
	line-height: 1.35;
	color: #ffffff;
	background-color: rgba(108, 111, 115, 0.95);
	opacity: 0;
	visibility: hidden;
	pointer-events: none;
	white-space: normal;
	transition: opacity 0.12s ease;
	z-index: 12;
}

.lecture-audio .vjs-segment-tooltip__text {
	display: block;
	max-width: none;
	white-space: nowrap;
}

.lecture-audio .vjs-progress-control,
.lecture-audio .vjs-progress-holder {
	overflow: visible !important;
}

.lecture-audio .vjs-segment-tooltip.is-visible {
	opacity: 1;
	visibility: visible;
}

.lecture-audio .vjs-segment-highlight {
	position: absolute;
	top: 50%;
	left: 0;
	width: 0;
	height: 10px;
	transform: translateY(-50%);
	border-radius: 999px;
	background-color: rgba(107, 114, 128, 0.24);
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.12s ease;
	z-index: 6;
}

.lecture-audio .vjs-segment-highlight.is-visible {
	opacity: 1;
}
</style>
